import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useReducer, } from 'react';
import { DayTimetable, HeatingCircuit, HeatingMode, isNotOptimized, WeekTimetable, } from 'z3k-config';
import { useBox } from 'shared/hooks/utils';
import { Box, Typography } from '@mui/material';
import { ScreenRotation } from '@mui/icons-material';
import { getRealScreenWidth } from '../../../utils/getRealScreenWidth';
import { getModeColor } from '../../../utils/ColorPicker4bit';
import { heating_modes_options_selector } from 'z3k-parser/lib/z3k-selectors';
import ScheduleElement from 'console/tabs/legacy/schedule-element';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { TemperatureSettingType } from './temperature-setting-type';
import { c2k, k2c, removeNulls } from 'utils';
const isModeOnTimeTable = (temp) => temp === 0 || temp === 1 || temp === 2 || temp === 255 || seemsLikeObjectId(temp);
import { seemsLikeObjectId } from 'z3k-parser/lib';
export const getTempOrModeFromTimetable = (temp) => (isModeOnTimeTable(temp) ? 'M' + temp : k2c(temp));
export const getTempOrModeFromSchedule = (t) => typeof t === 'number' ? c2k(t) : t[0] === 'M' ? +t.slice(1) : 0;
const NeedRotateScreen = memo(function NeedRotateScreenImpl() {
    return (_jsxs(Box, { display: "flex", alignItems: "center", children: [_jsx(Box, { mr: 2, children: _jsx(ScreenRotation, { color: "error" }) }), _jsx(Typography, { variant: "subtitle1", color: "error", children: "\u0414\u043B\u044F \u0440\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F \u043F\u043E\u0432\u0435\u0440\u043D\u0438\u0442\u0435 \u0442\u0435\u043B\u0435\u0444\u043E\u043D \u0432 \u0433\u043E\u0440\u0438\u0437\u043E\u043D\u0442\u0430\u043B\u044C\u043D\u043E\u0435 \u043F\u043E\u043B\u043E\u0436\u0435\u043D\u0438\u0435" })] }));
});
function WeekDaySchedulesImpl(props) {
    const { hm, type, is_boiler_zone, day_timetable, week_timetable, temp_setting_id, day_schedule_data, week_schedule_data, onClickOptimize, } = props;
    const { deviceFirmwareVersion } = useContext(Z3KSettings);
    const { config, replace_config: onChangeConfig } = useContext(Z3KSettingsEditor);
    const configBox = useBox(config);
    const [x, forceUpdate] = useReducer((x) => x + 1, 0);
    useEffect(() => {
        window.addEventListener('orientationchange', forceUpdate);
        return () => window.removeEventListener('orientationchange', forceUpdate);
    }, []);
    const is_large_screen = useMemo(() => getRealScreenWidth() >= 540, [x]);
    const heating_modes_options = useMemo(() => heating_modes_options_selector(config, hm.id), [config, hm.id]);
    const isBoilerMode = (option) => {
        if (option.heating_zones[0]) {
            const heating_circuit = HeatingCircuit.get.one(config, 
            // prettier-ignore
            ({ id }) => id === option.heating_zones[0].heating_circuit);
            return !!(heating_circuit && HeatingCircuit.isBoiler(heating_circuit));
        }
        return false;
    };
    const modes = useMemo(() => is_boiler_zone
        ? Object.fromEntries(removeNulls([
            [HeatingMode.OFF_MODE, { name: 'ВЫКЛ', zone_temp: [null, 30] }],
            [
                HeatingMode.REQUEST_MODE,
                { name: 'ПО ЗАПРОСУ', zone_temp: [null, 20] },
            ],
            deviceFirmwareVersion >= 71
                ? [
                    HeatingMode.ALWAYS_ON_MODE,
                    { name: 'ПОСТОЯННО', zone_temp: [null, 10] },
                ]
                : null,
            [
                HeatingMode.RESERVE_MODE,
                { name: 'РЕЗЕРВ', zone_temp: [null, 23] },
            ],
            ...heating_modes_options[0].options.map((item, index) => isBoilerMode(item)
                ? [
                    item.id,
                    {
                        name: item.name,
                        color: getModeColor(item, index),
                    },
                ]
                : null),
        ]))
        : Object.fromEntries(removeNulls(heating_modes_options[0].options.map((item, index) => !isBoilerMode(item)
            ? [
                item.id,
                {
                    name: item.name,
                    color: getModeColor(item, index),
                },
            ]
            : null))), [is_boiler_zone, deviceFirmwareVersion, heating_modes_options]);
    const onChangeDay = useCallback((data) => onChangeConfig(DayTimetable.get.applySetterById(configBox.current, temp_setting_id, {
        temp24: data[0].map(getTempOrModeFromSchedule),
    })), [onChangeConfig, temp_setting_id]);
    const onChangeWeek = useCallback((data) => {
        const current_week = WeekTimetable.get.byId(configBox.current, temp_setting_id);
        if (current_week == null) {
            return;
        }
        const week_day_hours = new Array(7)
            .fill(0)
            .map((v, i) => data[i].map(getTempOrModeFromSchedule));
        onChangeConfig(current_week.temp7.reduce((current_config, day_temp_id, index) => DayTimetable.get.applySetterById(current_config, day_temp_id, {
            temp24: week_day_hours[index],
        }), configBox.current));
    }, [onChangeConfig, temp_setting_id]);
    if (![TemperatureSettingType.day, TemperatureSettingType.week].includes(type)) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [type === TemperatureSettingType.day && day_timetable && (_jsx(_Fragment, { children: is_large_screen ? (_jsx(ScheduleElement, { day: true, tempschedule: [day_schedule_data], 
                    // @ts-ignore не соответствует типам для старых приборов, но работает :)
                    modes: modes, show_temperature_selector: !is_boiler_zone, onScheduleChanged: onChangeDay })) : (_jsx(NeedRotateScreen, {})) })), type === TemperatureSettingType.week && week_timetable && (_jsx(_Fragment, { children: is_large_screen ? (_jsx(ScheduleElement, { tempschedule: week_schedule_data, 
                    // @ts-ignore не соответствует типам для старых приборов, но работает :)
                    modes: modes, show_temperature_selector: !is_boiler_zone, onScheduleChanged: onChangeWeek })) : (_jsx(NeedRotateScreen, {})) })), type === TemperatureSettingType.week &&
                week_schedule_data?.[0]?.[0] !== undefined &&
                isNotOptimized(week_schedule_data) && (_jsxs("div", { className: "is-not-optimized", children: ["\u0422\u0430\u043A\u043E\u0435 \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u0435 \u043C\u043E\u0436\u0435\u0442 \u0431\u044B\u0442\u044C \u043F\u0440\u0435\u043E\u0431\u0440\u0430\u0437\u043E\u0432\u0430\u043D\u043E \u0432 \u0414\u043D\u0435\u0432\u043D\u043E\u0435. \u0420\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u0443\u0435\u043C \u0441\u0434\u0435\u043B\u0430\u0442\u044C \u044D\u0442\u043E \u0434\u043B\u044F \u043E\u043F\u0442\u0438\u043C\u0430\u043B\u044C\u043D\u043E\u0439 \u0440\u0430\u0431\u043E\u0442\u044B \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430.", ' ', _jsxs("a", { onClick: onClickOptimize, children: [_jsx("br", {}), "\u041F\u0440\u0435\u043E\u0431\u0440\u0430\u0437\u043E\u0432\u0430\u0442\u044C"] })] }))] }));
}
export const WeekDaySchedules = memo(WeekDaySchedulesImpl);
